import { useMemo, useState } from "react";
import {
    Button,
    Card,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
} from "@jhool-io/fe-components";
import { useParams, useSearchParams } from "react-router-dom";
import ListState from "../../../../components/ListState/ListState";
import { useTriggerSSOLaunch } from "../../hooks/e-prescribe.mutations";
import { useAppSelector } from "../../../../hooks/useRedux";
import useToast from "../../../../hooks/useToast";
import { ISSOLaunchResponse } from "../../types/e-prescribe.types";
import DrFirstIframe from "../DrFirstIframe/DrFirstIframe";
import { useGetClientAllergies } from "../../hooks/e-prescribe.queries";
import Loader from "../../../../shared-ui/Loader/Loader";
import ClientAllergyListTable from "./ClientAllergyListTable";
import {
    formatDate,
    getUniqueValuesFromList,
    truncateString,
} from "../../../../utils/helpers";
import { getFilterButtonText } from "../../helpers/e-prescribe.helpers";

interface ClientAllergyListProps {
    sessionHistoryId?: string;
}

export default function ClientAllergyList({
    sessionHistoryId,
}: ClientAllergyListProps) {
    const [launchData, setLaunchData] = useState<ISSOLaunchResponse | null>(
        null
    );
    const { practice } = useAppSelector((state) => state.userPractice);
    const params = useParams();
    const { clientId } = params;
    const [searchParams, setSearchParams] = useSearchParams();

    const nameFilter = searchParams.get("name") ?? "";
    const codeFilter = searchParams.get("code") ?? "";
    const codeSystemFilter = searchParams.get("codeSystem") ?? "";
    const reactionFilter = searchParams.get("reaction") ?? "";
    const severityFilter = searchParams.get("severity") ?? "";
    const statusFilter = searchParams.get("status") ?? "";
    const onsetDateFilter = searchParams.get("onsetDate") ?? "";
    const createdDateFilter = searchParams.get("createdDate") ?? "";

    const triggerSSOLaunch = useTriggerSSOLaunch();

    const { data, isLoading, error, isSuccess } = useGetClientAllergies({
        practiceId: practice?.practice_id ?? "",
        clientId: clientId ?? "",
        filters: { session_history_id: sessionHistoryId },
    });

    const actionButtonText = () => {
        if (isLoading) return "Loading...";
        if (isSuccess && data.data.length === 0) return "Add Allergy";
        if (isSuccess && data.data.length === 1) return "Update Allergy";
        return "Update Allergies";
    };

    const { toast } = useToast();

    const allergyNamesInList = useMemo(
        () =>
            getUniqueValuesFromList(
                data?.data || [],
                (allergy) => allergy.allergy_name
            ),
        [data?.data]
    );
    const allergyCodesInList = useMemo(
        () =>
            getUniqueValuesFromList(
                data?.data || [],
                (allergy) => allergy.allergy_code
            ),
        [data?.data]
    );
    const allergyCodeSystemsInList = useMemo(
        () =>
            getUniqueValuesFromList(
                data?.data || [],
                (allergy) => allergy.code_system
            ),
        [data?.data]
    );
    const allergyReactionsInList = useMemo(
        () =>
            getUniqueValuesFromList(
                data?.data || [],
                (allergy) => allergy.reaction
            ),
        [data?.data]
    );
    const allergySeveritiesInList = useMemo(
        () =>
            getUniqueValuesFromList(
                data?.data || [],
                (allergy) => allergy.severity
            ),
        [data?.data]
    );
    const allergyStatusesInList = useMemo(
        () =>
            getUniqueValuesFromList(
                data?.data || [],
                (allergy) => allergy.status
            ),
        [data?.data]
    );
    const allergyOnsetDatesInList = useMemo(
        () =>
            getUniqueValuesFromList(data?.data || [], (allergy) =>
                allergy.onset_date ? formatDate(allergy.onset_date, true) : null
            ),
        [data?.data]
    );
    const allergyCreatedDatesInList = useMemo(
        () =>
            getUniqueValuesFromList(data?.data || [], (allergy) =>
                allergy.created_at ? formatDate(allergy.created_at) : null
            ),
        [data?.data]
    );

    const handleFilterChange = (paramName: string, value: string) => {
        if (value === "All") searchParams.delete(paramName);
        else {
            searchParams.set(paramName, value);
        }
        setSearchParams(searchParams);
    };

    const filteredData = useMemo(() => {
        if (!data?.data) return [];

        return data.data.filter((allergy) => {
            if (nameFilter && allergy.allergy_name !== nameFilter) return false;

            if (codeFilter && allergy.allergy_code !== codeFilter) return false;

            if (codeSystemFilter && allergy.code_system !== codeSystemFilter)
                return false;

            if (reactionFilter && allergy.reaction !== reactionFilter)
                return false;

            if (severityFilter && allergy.severity !== severityFilter)
                return false;

            if (statusFilter && allergy.status !== statusFilter) return false;

            if (
                onsetDateFilter &&
                (!allergy.onset_date ||
                    formatDate(allergy.onset_date, true) !== onsetDateFilter)
            )
                return false;

            if (
                createdDateFilter &&
                (!allergy.created_at ||
                    formatDate(allergy.created_at) !== createdDateFilter)
            )
                return false;

            return true;
        });
    }, [
        data?.data,
        nameFilter,
        codeFilter,
        codeSystemFilter,
        reactionFilter,
        severityFilter,
        statusFilter,
        onsetDateFilter,
        createdDateFilter,
    ]);

    const handleTriggerSSOLaunch = async () => {
        triggerSSOLaunch.mutate(
            {
                practiceId: practice?.practice_id ?? "",
                payload: {
                    landing_screen: "manage_allergies",
                    session_history_id: sessionHistoryId,
                    client_id: clientId ?? "",
                },
            },

            {
                onSuccess: (res) => {
                    setLaunchData(res.data);
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err?.response?.data?.message ??
                            "Cannot update allergies at this time",
                    });
                },
            }
        );
    };

    return (
        <>
            <DrFirstIframe
                launchData={launchData}
                onClose={() => setLaunchData(null)}
                title="Update Allergies"
                clientId={clientId ?? ""}
                practiceId={practice?.practice_id ?? ""}
            />
            <div>
                <Card classNames="mb-[--spacing-header]">
                    <div className="flex flex-col gap-y-[--spacing-items]">
                        <div className="flex items-center justify-between gap-[--spacing-items] flex-wrap">
                            <div className="flex items-center gap-[--spacing-items] flex-wrap">
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Name",
                                                nameFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="150px">
                                        {["All", ...allergyNamesInList].map(
                                            (name) => (
                                                <DropdownItem
                                                    key={name}
                                                    onClick={() =>
                                                        handleFilterChange(
                                                            "name",
                                                            name
                                                        )
                                                    }
                                                >
                                                    {truncateString(name, 20)}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Code",
                                                codeFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="150px">
                                        {["All", ...allergyCodesInList].map(
                                            (code) => (
                                                <DropdownItem
                                                    key={code}
                                                    onClick={() =>
                                                        handleFilterChange(
                                                            "code",
                                                            code
                                                        )
                                                    }
                                                >
                                                    {truncateString(code, 20)}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Code System",
                                                codeSystemFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="150px">
                                        {[
                                            "All",
                                            ...allergyCodeSystemsInList,
                                        ].map((system) => (
                                            <DropdownItem
                                                key={system}
                                                onClick={() =>
                                                    handleFilterChange(
                                                        "codeSystem",
                                                        system
                                                    )
                                                }
                                            >
                                                {truncateString(system, 20)}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Reaction",
                                                reactionFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="150px">
                                        {["All", ...allergyReactionsInList].map(
                                            (reaction) => (
                                                <DropdownItem
                                                    key={reaction}
                                                    onClick={() =>
                                                        handleFilterChange(
                                                            "reaction",
                                                            reaction
                                                        )
                                                    }
                                                >
                                                    {truncateString(
                                                        reaction,
                                                        20
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Severity",
                                                severityFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="150px">
                                        {[
                                            "All",
                                            ...allergySeveritiesInList,
                                        ].map((severity) => (
                                            <DropdownItem
                                                key={severity}
                                                onClick={() =>
                                                    handleFilterChange(
                                                        "severity",
                                                        severity
                                                    )
                                                }
                                            >
                                                {truncateString(severity, 20)}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Status",
                                                statusFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="150px">
                                        {["All", ...allergyStatusesInList].map(
                                            (status) => (
                                                <DropdownItem
                                                    key={status}
                                                    onClick={() =>
                                                        handleFilterChange(
                                                            "status",
                                                            status
                                                        )
                                                    }
                                                >
                                                    {truncateString(status, 15)}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Onset Date",
                                                onsetDateFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        {[
                                            "All",
                                            ...allergyOnsetDatesInList,
                                        ].map((date) => (
                                            <DropdownItem
                                                key={date}
                                                onClick={() =>
                                                    handleFilterChange(
                                                        "onsetDate",
                                                        date
                                                    )
                                                }
                                            >
                                                {date}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Date Added",
                                                createdDateFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        {[
                                            "All",
                                            ...allergyCreatedDatesInList,
                                        ].map((date) => (
                                            <DropdownItem
                                                key={date}
                                                onClick={() =>
                                                    handleFilterChange(
                                                        "createdDate",
                                                        date
                                                    )
                                                }
                                            >
                                                {date}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                            </div>
                            {!error ? (
                                <div>
                                    <Button
                                        disabled={
                                            triggerSSOLaunch.isLoading ||
                                            isLoading
                                        }
                                        onClick={handleTriggerSSOLaunch}
                                    >
                                        {actionButtonText()}
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </Card>
                {isLoading && <Loader />}
                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display client allergies`
                        }
                    />
                )}
                {isSuccess && filteredData.length === 0 && (
                    <ListState
                        isError={false}
                        stateHelperText="Allergies will appear here when available"
                        emptyMessage="No allergies to display"
                    />
                )}
                {isSuccess && filteredData.length > 0 && (
                    <ClientAllergyListTable data={filteredData} />
                )}
            </div>
        </>
    );
}
